import { create } from 'zustand';

import { SECURE_API } from 'src/api/api';
import {
  SponsorsPublishedFilter,
  SponsorsSortBy,
} from 'src/helpers/filterSponsors';
import { EOrder } from 'src/interfaces/Order';
import { IBaseSponsor, ISponsor } from 'src/interfaces/Sponsor';
import { runApiAction } from 'src/stores/lib/runApiAction';

interface SponsorsState {
  sponsors: ISponsor[];
  isLoading: boolean;
  error: unknown | null;
  search: string;
  publishedFilter: SponsorsPublishedFilter;
  sortBy: SponsorsSortBy;
  sortOrder: EOrder;
  setSearch: (search: string) => void;
  setPublishedFilter: (filter: SponsorsPublishedFilter) => void;
  setSortBy: (sortBy: SponsorsSortBy) => void;
  setSortOrder: (order: EOrder) => void;
  fetchSponsors: (publishedOnly?: boolean) => Promise<void>;
  saveSponsor: (sponsor: IBaseSponsor, id?: string) => Promise<void>;
  deleteSponsor: (id: string) => Promise<void>;
  reorderSponsors: (orderedIds: string[]) => Promise<void>;
}

const useSponsorsStore = create<SponsorsState>((set, get) => ({
  sponsors: [],
  isLoading: false,
  error: null,
  search: '',
  publishedFilter: 'all',
  sortBy: 'sortOrder',
  sortOrder: EOrder.asc,
  setSearch: search => set({ search }),
  setPublishedFilter: publishedFilter => set({ publishedFilter }),
  setSortBy: sortBy => set({ sortBy }),
  setSortOrder: sortOrder => set({ sortOrder }),

  fetchSponsors: async (publishedOnly = false) => {
    await runApiAction({
      set,
      setLoading: true,
      clearLoadingOnFinish: true,
      onError: error => set({ error, sponsors: [] }),
      onSuccess: data => set({ sponsors: data as ISponsor[] }),
      action: async () => {
        const url = publishedOnly ? 'sponsors/?published=true' : 'sponsors/';
        const { data } = await SECURE_API.get<ISponsor[]>(url);
        return data;
      },
    });
  },

  saveSponsor: async (sponsor, id) => {
    await runApiAction({
      set,
      setLoading: true,
      clearLoadingOnFinish: true,
      onError: error => set({ error }),
      action: async () => {
        if (id) {
          await SECURE_API.put(`sponsors/${id}`, sponsor);
        } else {
          await SECURE_API.post('sponsors/', sponsor);
        }
      },
    });
    await get().fetchSponsors();
  },

  deleteSponsor: async id => {
    await runApiAction({
      set,
      setLoading: true,
      clearLoadingOnFinish: true,
      onError: error => set({ error }),
      action: async () => {
        await SECURE_API.delete(`sponsors/${id}`);
      },
    });
    await get().fetchSponsors();
  },

  reorderSponsors: async orderedIds => {
    await runApiAction({
      set,
      onError: error => set({ error }),
      action: async () => {
        await SECURE_API.put('sponsors/reorder/all', { orderedIds });
      },
    });
  },
}));

export default useSponsorsStore;
